import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { ArrowRight, Gavel, Plus, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  connectGoogleCalendar,
  fetchCalendarEvents,
  fetchGoogleCalendarStatus,
  syncGoogleCalendar,
  type CalendarEvent,
} from '../../api/client';
import { PautaDeSessoes } from '../../components/calendario/PautaDeSessoes';
import { ComunicacaoSheet } from '../../components/comunicacoes/ComunicacaoSheet';
import { EventDetailDialog } from '../../components/EventDetailDialog';
import { EventFormDialog } from '../../components/EventFormDialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { EmptyState } from '../../components/ui/EmptyState';
import { GoogleIcon } from '../../components/ui/GoogleIcon';
import { Spinner } from '../../components/ui/spinner';
import { notifyMutationError } from '../../lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';
import { cn } from '../../lib/utils';
import { formatRelativeTime } from './helpers';
import { useAtuacao } from '@/lib/delegacao/atuacao';

const DIAS = 7;
/** Uma busca por tipo: com a janela inteira, os prazos de uma semana cheia
 *  ocupavam o limite da listagem e empurravam as sessões para fora. */
const LIMITE_POR_TIPO = 200;

/** Janela de hoje até hoje + 7, calculada na hora da busca. */
function janela() {
  const agora = new Date();
  const inicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const fim = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + DIAS, 23, 59, 59, 999);
  return { inicio, fim };
}

/** A próxima hora cheia de hoje: o horário de partida da audiência marcada à mão. */
function proximaHoraCheia() {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return d;
}

/**
 * Card "Sessões e audiências nos próximos 7 dias" do dashboard, no desenho da
 * Agenda do Calendário (`PautaDeSessoes`): cada sessão se abre em relator e
 * processos; a sessão virtual em curso entra, com "em curso". Os prazos já estão
 * na régua, acima (docs/PLANO_DEDUP_COMUNICACOES.md §2.5).
 */
export function SessoesAudienciasCard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [comunicacaoSheetId, setComunicacaoSheetId] = useState<string | null>(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  // A chave não leva o dia (como a Agenda do Calendário): aberto offline no dia
  // seguinte, o card usa o cache em vez de uma chave nova e vazia.
  const { data: eventos = [], isLoading, error } = useQuery({
    queryKey: [...queryKeys.calendario.agenda, 'pauta-semana'],
    queryFn: async () => {
      const { inicio, fim } = janela();
      const [sessoes, audiencias] = await Promise.all(
        ['sessao', 'audiencia'].map((tipo) =>
          fetchCalendarEvents(inicio.toISOString(), fim.toISOString(), undefined, LIMITE_POR_TIPO, tipo),
        ),
      );
      return [...sessoes, ...audiencias];
    },
    meta: queryPersistMeta.nonEmpty,
  });

  // O que ainda não acabou: a sessão virtual aberta ontem e encerrada daqui a
  // três dias continua na lista.
  const pauta = useMemo(() => {
    const { inicio } = janela();
    return eventos.filter((e) => parseISO(e.data_fim ?? e.data_inicio) >= inicio);
  }, [eventos]);

  // Integração com Google Calendar (atalho do dashboard). É do titular como pessoa:
  // na conta dele, o delegado não conecta nem sincroniza (rota não delegável).
  const atuacao = useAtuacao();
  const { data: googleStatus } = useQuery({
    queryKey: [...queryKeys.calendario.googleStatus],
    queryFn: fetchGoogleCalendarStatus,
    meta: queryPersistMeta.never,
    enabled: !atuacao,
  });

  const syncMutation = useMutation({
    mutationFn: syncGoogleCalendar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.calendario.agenda] });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.calendario.eventos] });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.calendario.googleStatus] });
      toast.success('Calendário sincronizado com sucesso');
    },
    onError: (err) => notifyMutationError(err, toast.error),
  });

  const handleConnectGoogle = async () => {
    try {
      const { auth_url } = await connectGoogleCalendar();
      window.location.href = auth_url;
    } catch (err) {
      notifyMutationError(err, toast.error);
    }
  };

  return (
    <>
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-start gap-2 min-w-0">
              <Gavel className="h-5 w-5 shrink-0 text-primary mt-0.5" />
              <h3 className="text-base font-semibold">Sessões e audiências nos próximos {DIAS} dias</h3>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="text-primary h-auto py-1 px-2"
                aria-label="Novo evento"
                onClick={() => setEventDialogOpen(true)}
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-primary h-auto py-1 px-2"
                onClick={() => navigate('/app/calendario')}
              >
                Ver calendário
                <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-6">
              <Spinner size={32} label="Carregando sessões e audiências" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive py-1">Erro ao carregar a agenda</p>
          ) : pauta.length === 0 ? (
            <EmptyState icon={<Gavel />} title={`Nenhuma sessão ou audiência nos próximos ${DIAS} dias`} />
          ) : (
            <div className="max-h-[28rem] overflow-y-auto pr-1">
              <PautaDeSessoes events={pauta} onSelectEvent={setSelectedEvent} />
            </div>
          )}

          {/* Atalho de sincronização com Google Calendar */}
          {!atuacao && (
            <div className="mt-3 pt-3 border-t border-border">
              {googleStatus?.connected && (
                <div className="flex items-center gap-3 mb-3">
                  <GoogleIcon className="h-4 w-4 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Sincronizado</p>
                    <p className="text-xs text-muted-foreground">
                      {googleStatus.last_sync
                        ? formatRelativeTime(googleStatus.last_sync)
                        : 'Conectado ao Google Calendar'}
                    </p>
                  </div>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={googleStatus?.connected ? () => syncMutation.mutate() : handleConnectGoogle}
                disabled={syncMutation.isPending}
              >
                {googleStatus?.connected ? (
                  <>
                    <RefreshCw className={cn('mr-2 h-4 w-4', syncMutation.isPending && 'animate-spin')} />
                    {syncMutation.isPending ? 'Sincronizando...' : 'Sincronizar'}
                  </>
                ) : (
                  <>
                    <GoogleIcon className="mr-2 h-4 w-4" />
                    Sincronizar com Google Calendar
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <EventFormDialog
        open={eventDialogOpen}
        onClose={() => setEventDialogOpen(false)}
        // Nasce audiência (não o prazo padrão do formulário): o card só lista
        // sessões e audiências, e o evento criado aqui tem de aparecer aqui.
        defaultValues={{ tipo: 'audiencia', data_inicio: proximaHoraCheia().toISOString() }}
      />
      <EventDetailDialog
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onOpenComunicacao={setComunicacaoSheetId}
      />
      <ComunicacaoSheet comunicacaoId={comunicacaoSheetId} onClose={() => setComunicacaoSheetId(null)} />
    </>
  );
}
